import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useActionChoices } from "stores";
import { Button } from "components";
import { TASKS } from "constants/actions";
import { ACTION_STATUS } from "constants/actions";
import { useUserContext } from "context/UserProvider";
import { ReactComponent as UserAvatarIcon } from "assets/icons/user-avatar.svg";
import NormalText from "../../../../Typography/NormalText";

const statusTranslated = (status, t) => {
  switch (status) {
    case ACTION_STATUS.failed:
      return t("ActionModal.Badge.Failed");
    case ACTION_STATUS.suggested:
      return t("ActionModal.Badge.Suggested");
    case ACTION_STATUS.performed:
      return t("ActionModal.Badge.Performed");
    default:
      return "";
  }
};

const ViewActionModalHeader = ({
  action,
  onDelete,
  onToggleComplete,
  onEdit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { isSpecificAction } = useActionChoices();
  const { userData } = useUserContext();
  const { status } = action;
  const [finalStatus, setFinalStatus] = useState(status);

  useEffect(() => {
    if (status === ACTION_STATUS.suggested) {
      const compareDate = moment(action.action.dateRange.upper).endOf("day");
      const nowMoment = moment();
      if (compareDate.isBefore(nowMoment)) {
        setFinalStatus(ACTION_STATUS.failed);
      }
    }
  }, [action.action?.dateRange?.upper, status]);

  let badgeClassname = "action-status-badge";
  if (finalStatus === ACTION_STATUS.failed) {
    badgeClassname += " error";
  } else if (finalStatus === ACTION_STATUS.performed) {
    badgeClassname += " success";
  } else {
    badgeClassname += " pending";
  }

  const isOwner = userData?.id == action?.action?.createdByUserId ?? false;

  const isUncompleteAllowed = useMemo(() => {
    const taskId = action?.action?.taskId;
    const isObservation = isSpecificAction(TASKS.observation, taskId);
    return !isObservation;
  }, [action?.action?.taskId, isSpecificAction]);

  return (
    <>
      <div className="header-row space-between">
        <div className="header-title">{t("ActionModal.Mode.ViewAction")}</div>
        <div className="header-btn-group">
          {isOwner && (
            <>
              <Button
                isLoading={isLoading}
                isDisabled={isLoading}
                variant="text"
                corners="square"
                className="action-button"
                onClick={onDelete}
              >
                {t("Global.Delete")}
              </Button>
              <Button
                corners="square"
                className="action-button"
                onClick={onEdit}
              >
                {t("Global.Edit")}
              </Button>
            </>
          )}
          {isUncompleteAllowed && (
            <Button
              variant="filled"
              corners="square"
              className="action-button"
              onClick={onToggleComplete}
            >
              {t(
                status !== ACTION_STATUS.performed
                  ? "ActionModal.Complete"
                  : "ActionModal.UndoComplete",
              )}
            </Button>
          )}
        </div>
      </div>
      <div className="header-row">
        <div className={badgeClassname}>{statusTranslated(finalStatus, t)}</div>
        {action.action.createdAt && (
          <div className="action-assignment">
            <NormalText
              className="action-assignment-type"
              text={t("ActionModal.Assignment.Assigned")}
            />
            <UserAvatarIcon
              alt="user-avatar-icon"
              fill="#0F6CBD"
              width="20px"
              height="20px"
            />
            <NormalText
              text={moment(action.action.createdAt)?.format("DD MMM YYYY")}
            />
          </div>
        )}
        {action.action.datetimePerformedEnd && (
          <div className="action-assignment">
            <NormalText
              className="action-assignment-type"
              text={t("ActionModal.Assignment.Completion")}
            />
            <UserAvatarIcon
              alt="user-avatar-icon"
              fill="#FF8C00"
              width="20px"
              height="20px"
            />
            <NormalText
              text={moment(action.datetimePerformedEnd)?.format("DD MMM YYYY")}
            />
          </div>
        )}
      </div>
    </>
  );
};
export default ViewActionModalHeader;
